/**
 * Header Navigation Menu Toggle
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitTextReveal from './SplitTextReveal';

gsap.registerPlugin(ScrollTrigger);

class HeaderNav {
  constructor() {
    this.header = document.querySelector('.site-header');
    this.menuToggle = document.querySelector('.menu-toggle');
    this.menuCloseToggle = document.querySelector('.menu-close-toggle');
    this.primaryNav = document.getElementById('primary-menu');
    this.navHeader = document.querySelector('.nav-overlay-header');
    this.navSections = document.querySelectorAll('.nav-menu-section');
    this.navImage = document.querySelector('.nav-overlay-image');
    this.isOpen = false;
    this.isAnimating = false;
    this.timeline = null;

    if (this.menuToggle && this.primaryNav) {
      this.init();
    }
  }

  init() {
    this.menuToggle.addEventListener('click', () => {
      this.toggleMenu();
    });
    
    if (this.menuCloseToggle) {
      this.menuCloseToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.closeMenu();
      });
    }

    // Close menu on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
      }
    });

    // Close menu when clicking on a link
    const menuLinks = this.primaryNav.querySelectorAll('.nav-section-menu a');
    menuLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        // Don't close immediately - let the navigation happen
        setTimeout(() => this.closeMenu(), 100);
      });
    });
  }

  toggleMenu() {
    // Prevent toggling while animating
    if (this.isAnimating) {
      return;
    }

    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    if (this.isAnimating) {
      return;
    }
    
    this.isAnimating = true;
    this.isOpen = true;
    this.primaryNav.removeAttribute('hidden');
    this.menuToggle.setAttribute('aria-expanded', 'true');
    this.menuToggle.style.pointerEvents = 'none'; // Disable menu toggle during animation
    if (this.menuCloseToggle) {
      this.menuCloseToggle.setAttribute('aria-expanded', 'true');
      this.menuCloseToggle.style.pointerEvents = 'none'; // Disable close toggle during animation
    }
    document.body.style.overflow = 'hidden';
    
    // Create animation timeline
    this.timeline = gsap.timeline({
      onComplete: () => {
        this.isAnimating = false;
        this.menuToggle.style.pointerEvents = '';
        if (this.menuCloseToggle) {
          this.menuCloseToggle.style.pointerEvents = ''; // Re-enable after animation
        }
      }
    });

    // Set initial states
    gsap.set(this.primaryNav, { 
      display: 'flex',
      clipPath: 'inset(0 0 100% 0)' // Full overlay expands from top
    });
    gsap.set(this.navSections, { 
      opacity: 0, 
      y: 40 
    });
    gsap.set(this.navImage, { 
      opacity: 0,
      x: -60
    });
    // Hide nav titles initially
    const navTitles = this.primaryNav.querySelectorAll('.nav-section-title');
    gsap.set(navTitles, { 
      opacity: 0 
    });
    // Hide nav menu items initially
    const navMenuItems = this.primaryNav.querySelectorAll('.nav-section-menu li');
    gsap.set(navMenuItems, {
      opacity: 0,
      y: 20
    });

    this.timeline
      // Step 1: Expand green background from top, revealing the static header
      .to(this.primaryNav, {
        clipPath: 'inset(0 0 0% 0)',
        duration: 0.5,
        ease: 'power2.out'
      })
      // Step 2: Fade up menu sections staggered
      .to(this.navSections, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.1,
        ease: 'power2.out',
        onStart: () => {
          // Initialize split text reveal animations as sections start to fade in
          this.initNavSectionTitleAnimations();
          // Animate menu items with delay
          setTimeout(() => {
            this.animateNavMenuItems();
          }, 100);
        }
      }, '-=0.3')
      // Step 3: Image fades in and slides to the right into position
      .to(this.navImage, {
        opacity: 1,
        x: 0,
        duration: 0.5,
        ease: 'power2.out'
      }, '-=0.4');
  }

  closeMenu() {
    if (this.isAnimating) {
      return;
    }
    
    this.isAnimating = true;
    this.isOpen = false;
    this.menuToggle.setAttribute('aria-expanded', 'false');
    this.menuToggle.style.pointerEvents = 'none'; // Disable during close animation
    if (this.menuCloseToggle) {
      this.menuCloseToggle.setAttribute('aria-expanded', 'false');
      this.menuCloseToggle.style.pointerEvents = 'none'; // Disable during close animation
    }
    document.body.style.overflow = '';
    
    // Kill any existing animation
    if (this.timeline) {
      this.timeline.kill();
    }

    // Reverse animation timeline
    this.timeline = gsap.timeline({
      onComplete: () => {
        this.primaryNav.setAttribute('hidden', '');
        gsap.set(this.primaryNav, { 
          clipPath: 'none', // Reset clip-path on primaryNav
          display: '' // Remove inline display style so hidden attribute works
        });
        // Reset nav titles to original text
        this.resetNavSectionTitles();        // Reset menu items
        const navMenuItems = this.primaryNav.querySelectorAll('.nav-section-menu li');
        gsap.set(navMenuItems, {
          opacity: 0,
          y: 20
        });        this.isAnimating = false;
        this.menuToggle.style.pointerEvents = ''; // Re-enable menu toggle
        if (this.menuCloseToggle) {
          this.menuCloseToggle.style.pointerEvents = '';
        }
      }
    });

    this.timeline
      // Step 1: Image fades out and slides to the left
      .to(this.navImage, {
        opacity: 0,
        x: -60,
        duration: 0.4,
        ease: 'power2.in'
      })
      // Step 2: Menu sections fade down staggered (reverse order)
      .to([...this.navSections].reverse(), {
        opacity: 0,
        y: 40,
        duration: 0.3,
        stagger: 0.08,
        ease: 'power2.in'
      }, '-=0.25')
      // Step 3: Green background collapses to top
      .to(this.primaryNav, {
        clipPath: 'inset(0 0 100% 0)',
        duration: 0.4,
        ease: 'power2.in'
      }, '-=0.2');
  }

  /**
   * Initialize split text reveal animations for nav section titles
   */
  initNavSectionTitleAnimations() {
    const navTitles = this.primaryNav.querySelectorAll('.nav-section-title[data-split-text-reveal]');
    
    navTitles.forEach((title, index) => {
      // Get delay from data attribute
      const delay = parseFloat(title.dataset.splitDelay) || (index * 0.2);
      
      // Store original text if not already stored
      if (!title.dataset.originalText) {
        title.dataset.originalText = title.textContent;
      }
      const originalText = title.dataset.originalText;
      const words = originalText.split(' ');
      
      // Clear the title and rebuild with character spans
      title.innerHTML = '';
      
      // Show the title now that we're ready to animate
      gsap.set(title, { opacity: 1 });
      
      words.forEach((word, wordIndex) => {
        // Create word container to keep characters together
        const wordSpan = document.createElement('span');
        wordSpan.style.display = 'inline-block';
        wordSpan.style.whiteSpace = 'nowrap';
        
        // Split word into characters
        const chars = word.split('');
        const charElements = [];
        
        chars.forEach(char => {
          const charMask = document.createElement('span');
          charMask.setAttribute('data-char-mask', '');
          charMask.style.overflow = 'hidden';
          charMask.style.display = 'inline-block';
          charMask.style.verticalAlign = 'bottom';
          
          const charInner = document.createElement('span');
          charInner.setAttribute('data-char-inner', '');
          charInner.style.display = 'inline-block';
          charInner.textContent = char;
          
          charMask.appendChild(charInner);
          wordSpan.appendChild(charMask);
          charElements.push(charInner);
        });
        
        title.appendChild(wordSpan);
        
        // Add space after word (except for last word)
        if (wordIndex < words.length - 1) {
          const spaceSpan = document.createElement('span');
          spaceSpan.innerHTML = '&nbsp;';
          title.appendChild(spaceSpan);
        }
        
        // Set initial state for characters
        gsap.set(charElements, {
          yPercent: 100,
          opacity: 0
        });
        
        // Animate characters in
        gsap.to(charElements, {
          yPercent: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.03,
          ease: 'power2.out',
          delay: delay + (wordIndex * 0.06),
          onComplete: () => {
            // Clean up masks after animation
            charElements.forEach(charEl => {
              if (charEl.parentElement) {
                const mask = charEl.parentElement;
                mask.style.overflow = '';
              }
            });
          }
        });
      });
    });
  }

  /**
   * Reset nav section titles to original text and hide them
   */
  resetNavSectionTitles() {
    const navTitles = this.primaryNav.querySelectorAll('.nav-section-title[data-split-text-reveal]');
    navTitles.forEach(title => {
      if (title.dataset.originalText) {
        title.innerHTML = title.dataset.originalText;
      }
      gsap.set(title, { opacity: 0 });
    });
  }

  /**
   * Animate nav menu items (links) with cascading fade up
   */
  animateNavMenuItems() {
    this.navSections.forEach((section, sectionIndex) => {
      const menuItems = section.querySelectorAll('.nav-section-menu li');
      const sectionDelay = sectionIndex * 0.1; // Match section stagger
      
      gsap.to(menuItems, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        stagger: 0.05,
        ease: 'power2.out',
        delay: sectionDelay + 0.15 // Delay after heading starts animating
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HeaderNav();
  });
} else {
  new HeaderNav();
}

export default HeaderNav;
